import { functionDoc } from '../functionDoc';
import { ROLES, SHOTS } from '../docHelpers';

/** Documentación detallada (plantilla 12 apartados) — Administración. */
export const administracionSections = [
  {
    id: "adm-traz-documentos",
    name: "Trazabilidad de documentos",
    type: "screen",
    route: "/administration/document-traceability",
    locationPath: "Administración → Trazabilidad → Trazabilidad de Documentos",
    generalDescription: "Rastrea un documento de compra (factura, boleta, etc.) a través de OC, guías y pagos relacionados.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Búsqueda por código/ID",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Identificador del documento.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Diagrama o línea de tiempo",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Cadena documental.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Ingrese número o ID.",
      "Revise cada eslabón.",
      "Exporte o anote hallazgos para compras."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Trazabilidad de documentos"
      },
      steps: [
        "Ingrese número o ID.",
        "Revise cada eslabón.",
        "Exporte o anote hallazgos para compras."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "adm-traz-oc"
    ],
    observations: [],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad)."
  },

  {
    id: "adm-traz-oc",
    name: "Trazabilidad de órdenes de compra",
    type: "screen",
    route: "/administration/purchase-order-traceability",
    locationPath: "Administración → Trazabilidad → Trazabilidad de Órdenes de Compra",
    generalDescription: "Estado de atención de una OC: cantidades pedidas vs recibidas y facturadas.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Código OC",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Búsqueda principal.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Detalle de líneas",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Pendientes y cerradas.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Busque OC.",
      "Identifique guías o facturas faltantes.",
      "Coordine con almacén y compras."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Trazabilidad de órdenes de compra"
      },
      steps: [
        "Busque OC.",
        "Identifique guías o facturas faltantes.",
        "Coordine con almacén y compras."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "adm-traz-guias"
    ],
    observations: [],
    screenshots: [
      SHOTS.listado
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad)."
  },

  {
    id: "adm-traz-os",
    name: "Trazabilidad de órdenes de servicio",
    type: "screen",
    route: "/administration/service-order-traceability",
    locationPath: "Administración → Trazabilidad → Trazabilidad de Órdenes de Servicio",
    generalDescription: "Seguimiento de órdenes de servicio de compra hasta documentos y recepciones asociadas.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Código OS",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Búsqueda.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Documentos vinculados",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Facturas y estados.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Busque OS de compra.",
      "Revise documentos pendientes."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Trazabilidad de órdenes de servicio"
      },
      steps: [
        "Busque OS de compra.",
        "Revise documentos pendientes."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [],
    observations: [],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad)."
  },

  {
    id: "adm-traz-guias",
    name: "Trazabilidad de guías",
    type: "screen",
    route: "/administration/entry-guide-traceability",
    locationPath: "Administración → Trazabilidad → Trazabilidad de Guías",
    generalDescription: "Localiza una guía de ingreso y su relación con OC, documentos y kardex.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Número de guía",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Filtro principal.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Movimientos de stock",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Resumen kardex.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Ingrese guía.",
      "Valide cantidades vs OC.",
      "Investigue diferencias en almacén."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Trazabilidad de guías"
      },
      steps: [
        "Ingrese guía.",
        "Valide cantidades vs OC.",
        "Investigue diferencias en almacén."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "adm-traz-oc"
    ],
    observations: [],
    screenshots: [
      SHOTS.listado
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad)."
  },

  {
    id: "adm-traz-requerimientos",
    name: "Trazabilidad de requerimientos",
    type: "screen",
    route: "/administration/requirement-traceability",
    locationPath: "Administración → Trazabilidad → Trazabilidad de Requerimientos",
    generalDescription: "Desde un requerimiento interno hasta OC/OS y documentos que lo atendieron.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Código requerimiento",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Búsqueda.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Estado de atención",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Por línea.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Busque requerimiento.",
      "Verifique si tiene OC asociada.",
      "Cierre loop con compras."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Trazabilidad de requerimientos"
      },
      steps: [
        "Busque requerimiento.",
        "Verifique si tiene OC asociada.",
        "Cierre loop con compras."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [],
    observations: [],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad)."
  },

  {
    id: "adm-actividad",
    name: "Registro de actividad",
    type: "screen",
    route: "/administration/activity-logs",
    locationPath: "Administración → Registro de actividad",
    generalDescription: "Auditoría de acciones de usuarios: creaciones, ediciones y anulaciones sensibles filtrables por módulo y fecha.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Filtros usuario/módulo/fecha",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: false,
        description: "Acota el log.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tabla de eventos",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "Detalle JSON o resumen.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Filtre por usuario sospechoso o fecha.",
      "Abra detalle del evento.",
      "Correlacione con documentos afectados."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Registro de actividad"
      },
      steps: [
        "Filtre por usuario sospechoso o fecha.",
        "Abra detalle del evento.",
        "Correlacione con documentos afectados."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [],
    observations: [],
    screenshots: [
      SHOTS.listado
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad)."
  },

  {
    id: "adm-resultados",
    name: "Reporte de resultados",
    type: "report",
    route: "/administration/reports/results",
    locationPath: "Administración → Reportes → Resultados",
    generalDescription: "Estado de resultados administrativo: ingresos (ventas/servicios) y gastos (compras/costos) con reglas de prorrateo. Reporte pesado; puede tardar en años completos.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Año o rango",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Período fiscal.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Moneda de presentación",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "PEN/USD según dual currency.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Exportar Excel",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Si está disponible.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Seleccione año o rango.",
      "Espere carga completa.",
      "Analice ingresos vs gastos.",
      "Exporte para directorio."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Reporte de resultados"
      },
      steps: [
        "Seleccione año o rango.",
        "Espere carga completa.",
        "Analice ingresos vs gastos.",
        "Exporte para directorio."
      ]
    },
    validations: [
      "Requiere tipos de cambio para documentos en USD."
    ],
    expectedResult: [
      "Muestra ingresos y gastos del período con totales exportables."
    ],
    commonErrors: [
      {
        error: "Reporte no termina de cargar",
        cause: "Volumen alto de datos.",
        solution: "Acote el período o ejecute fuera de hora pico."
      }
    ],
    relatedFunctions: [
      "adm-tipos-cambio"
    ],
    observations: [],
    screenshots: [
      SHOTS.vistaGeneral
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad).",
    isVisible: (ctx) => ctx.limitedScope.showAdministracionCompleta !== false
  },

  {
    id: "adm-tipos-cambio",
    name: "Tipos de cambio",
    type: "form",
    route: "/administration/exchange-rates",
    locationPath: "Administración → Tipos de cambio",
    generalDescription: "Mantenimiento de tipo de cambio compra/venta por fecha para documentos dual currency y reportes en PEN.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Fecha",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Día hábil.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "TC compra / venta",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Valores SUNAT o manuales.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Sincronizar rango",
        controlType: "Botón",
        required: false,
        editable: true,
        automatic: false,
        description: "Job de actualización masiva si existe.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Registre TC faltantes.",
      "Sincronice rango histórico si aplica.",
      "Valide en un documento USD."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Tipos de cambio"
      },
      steps: [
        "Registre TC faltantes.",
        "Sincronice rango histórico si aplica.",
        "Valide en un documento USD."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "adm-resultados"
    ],
    observations: [],
    screenshots: [
      SHOTS.formularioCompleto
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad).",
    isVisible: (ctx) => ctx.limitedScope.showAdministracionCompleta !== false
  },

  {
    id: "adm-rendimiento-usuario",
    name: "Informe de rendimiento por usuario",
    type: "report",
    route: "/administration/user-performance-report",
    locationPath: "Administración → Informe de rendimiento por usuario",
    generalDescription: "Métricas de productividad por usuario (documentos creados, tiempos, módulos usados) según implementación del reporte.",
    prerequisites: [
      "Menú Administración visible para su usuario.",
      "Muchas pantallas requieren perfil con administración completa."
    ],
    interfaceElements: [
      {
        name: "Rango de fechas",
        controlType: "Campo",
        required: true,
        editable: true,
        automatic: false,
        description: "Período.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Usuario",
        controlType: "Select",
        required: false,
        editable: true,
        automatic: false,
        description: "Filtro opcional.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      },
      {
        name: "Tabla de indicadores",
        controlType: "Campo",
        required: false,
        editable: true,
        automatic: true,
        description: "KPI por usuario.",
        behavior: "Revise el comportamiento al interactuar en pantalla."
      }
    ],
    procedure: [
      "Defina período.",
      "Ejecute reporte.",
      "Identifique cuellos de botella operativos."
    ],
    practicalExample: {
      context: "Uso en operación diaria.",
      data: {
        Pantalla: "Informe de rendimiento por usuario"
      },
      steps: [
        "Defina período.",
        "Ejecute reporte.",
        "Identifique cuellos de botella operativos."
      ]
    },
    validations: ['Use identificadores existentes (código, ID o período) para obtener resultados.'],
    expectedResult: ['La pantalla o reporte muestra la trazabilidad o totales solicitados.'],
    commonErrors: [
      {
        error: "No veo el ítem en el menú lateral",
        cause: "Módulo desactivado o rol sin permiso.",
        solution: "Solicite al administrador habilitar el módulo y revisar el rol del usuario."
      },
      {
        error: "Error al guardar o cargar",
        cause: "Campos obligatorios vacíos, sesión expirada o error de red.",
        solution: "Complete validaciones, recargue la página o vuelva a iniciar sesión."
      }
    ],
    relatedFunctions: [
      "adm-actividad"
    ],
    observations: [],
    screenshots: [
      SHOTS.listado
    ],
    roles: "Administrador o usuario con alcance de administración (no solo registro de actividad).",
    isVisible: (ctx) => ctx.limitedScope.showAdministracionCompleta !== false
  }
].map((p) => functionDoc(p));
